<template>
  <div class="searchWrapper">
    <div class="pageTop">
      <div class="d-none d-md-block">
        <div class="d-flex flex-column">
          <b-breadcrumb :items="crumb_links"></b-breadcrumb>
          <div class="text-left ml-2 d-flex">
            <h4 style="color: #930">TAIR Protein Search</h4>
            <span class="ml-2"
              >[<a :href="helpLink" target="_blank">Help</a>]</span
            >
          </div>
          <div class="text-left m-2">
            This page allows you to search for protein information using a
            variety of parameters, including gene information and domain
            information.
          </div>
        </div>
      </div>
      <div class="d-md-none">
        <div class="row">
          <div class="col-4 font-weight-bold pl-2">Search Result</div>
          <div class="col-8">
            <b-dropdown id="dropdown-1" text="Menu" class="m-md-2">
              <b-dropdown-item>New Search</b-dropdown-item>
            </b-dropdown>
          </div>
        </div>
      </div>
    </div>
    <div class="pageMain m-2">
      <div class="d-flex flex-column">
        <div class="row mt-2">
          <div class="col-sm d-flex align-items-start ml-4">
            <button
              class="btn btn-secondary text-white btn-sm mr-2"
              @click="onReset"
              aria-label="Reset search form"
            >
              Reset
            </button>
            <button
              class="btn btn-primary text-white btn-sm"
              @click="onSubmit"
              aria-label="Submit gene search query"
            >
              Submit Query
            </button>
          </div>
        </div>
        <div>
          <div class="d-flex flex-column mx-4 my-2">
            <form-section
              v-for="(filter, index) in search_form.filters"
              :key="index"
              :filter="filter"
              @on-enter="onSubmit"
            />
            <protein-domain-rows
              v-model="domainRows"
              :error="domainError"
              @input="domainError = ''"
              @on-enter="onSubmit"
            />
          </div>
        </div>
        <div class="row mt-2">
          <div class="col-sm d-flex align-items-start ml-4 mb-4">
            <button
              class="btn btn-secondary text-white btn-sm mr-2"
              @click="onReset"
              aria-label="Reset search form"
            >
              Reset
            </button>
            <button
              class="btn btn-primary text-white btn-sm"
              @click="onSubmit"
              aria-label="Submit gene search query"
            >
              Submit Query
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FormSection from "../../components/common/FormSection.vue";
import ProteinDomainRows from "../../components/search/ProteinDomainRows.vue";
import { domainRowsToParams, newDomainRow } from "../../services/search/domainRows";

// Multi-selects hold arrays, so Reset must not share them with the live form.
function copyValue(value) {
  return value === undefined ? undefined : JSON.parse(JSON.stringify(value));
}
export default {
  name: "ProteinSearch",
  components: {
    FormSection,
    ProteinDomainRows,
  },
  data() {
    return {
      domainRows: [newDomainRow()],
      domainError: "",
      crumb_links: [
        {
          text: "Home",
          href: "/",
        },
        {
          text: "Search",
          href: "/search/overview",
        },
        {
          text: "Proteins",
          active: true,
        },
      ],
      search_form: {
        filters: [
          {
            label: "Search by Name or Class Type",
            type: "rows",
            rows: [
              {
                name: "Search Name:",
                value: {
                  type: "mixed",
                  elements: [
                    {
                      type: "select",
                      label: "Protein name search type",
                      options: [
                        { text: "Locus name", value: "locus" },
                        { text: "Gene Symbol", value: "symbol" },
                        { text: "GenPept ID", value: "genPeptId" },
                      ],
                      value: "locus",
                      id: "name_search_by",
                      size: "large",
                    },
                    {
                      type: "select",
                      label: "Protein name search method",
                      options: [
                        { text: "starts with", value: "starts" },
                        { text: "contains", value: "contains" },
                        { text: "exactly", value: "exactly" },
                      ],
                      value: "contains",
                      id: "name_search_type",
                      size: "medium",
                    },
                    {
                      type: "input",
                      label: "Protein name search input",
                      placeholder: "Search by name",
                      value: null,
                      id: "name_input",
                      size: "large",
                    },
                  ],
                },
              },
              {
                name: "Structural Class Type",
                value: {
                  type: "mixed",
                  elements: [
                    {
                      type: "multiSelect",
                      label:
                        "Protein structural class type (multiple selection)",
                      options: [
                        { value: "a", text: "All Alpha Proteins" },
                        { value: "b", text: "All Beta Proteins" },
                        { value: "c", text: "Alpha and beta proteins (a/b)" },
                        { value: "d", text: "Alpha and beta proteins (a+b)" },
                        {
                          value: "e",
                          text: "Multi-domain proteins (alpha and beta)",
                        },
                        {
                          value: "f",
                          text: "Membrane and cell surface proteins and peptides",
                        },
                        { value: "g", text: "Small proteins" },
                        { value: "h", text: "Coiled coil proteins" },
                      ],
                      value: [],
                      id: "class_type_multi",
                      size: 4,
                      width: "large",
                    },
                  ],
                },
              },
            ],
          },
          {
            label: "Search by Gene List",
            type: "rows",
            rows: [
              {
                name: "Upload or type in a list of AGI locus IDs (separated by line return)",
                value: {
                  type: "mixed",
                  elements: [
                    {
                      type: "multiInput",
                      label: "Gene list input",
                      value: "",
                      default: "",
                      id: "gene_list",
                    },
                  ],
                },
              },
            ],
          },
          {
            label: "Search by Physio-chemical Properties",
            type: "rows",
            rows: [
              {
                name: "Length",
                value: {
                  type: "mixed",
                  elements: [
                    {
                      type: "checkbox",
                      label: "Enable length filter",
                      value: false,
                      id: "length_checkbox",
                    },
                    {
                      type: "text",
                      value: "between",
                    },
                    {
                      type: "input",
                      label: "Minimum length (residues)",
                      value: 0,
                      id: "length_min",
                      size: "small",
                    },
                    {
                      type: "text",
                      value: "and",
                    },
                    {
                      type: "input",
                      label: "Maximum length (residues)",
                      value: 2000,
                      id: "length_max",
                      size: "small",
                    },
                    {
                      type: "text",
                      value: "residues",
                    },
                  ],
                },
              },
              {
                name: "Calculated MW",
                value: {
                  type: "mixed",
                  elements: [
                    {
                      type: "checkbox",
                      label: "Enable calculated MW filter",
                      value: false,
                      id: "calcmw_checkbox",
                    },
                    {
                      type: "text",
                      value: "between",
                    },
                    {
                      type: "input",
                      label: "Minimum calculated MW (kDa)",
                      value: 0,
                      id: "calcmw_min",
                      size: "small",
                    },
                    {
                      type: "text",
                      value: "and",
                    },
                    {
                      type: "input",
                      label: "Maximum calculated MW (kDa)",
                      value: 500,
                      id: "calcmw_max",
                      size: "small",
                    },
                    {
                      type: "text",
                      value: "kDa",
                    },
                  ],
                },
              },
              {
                name: "Calculated PI",
                value: {
                  type: "mixed",
                  elements: [
                    {
                      type: "checkbox",
                      label: "Enable calculated PI filter",
                      value: false,
                      id: "calcpi_checkbox",
                    },
                    {
                      type: "text",
                      value: "between",
                    },
                    {
                      type: "input",
                      label: "Minimum calculated PI",
                      value: 1,
                      id: "calcpi_min",
                      size: "small",
                    },
                    {
                      type: "text",
                      value: "and",
                    },
                    {
                      type: "input",
                      label: "Maximum calculated PI",
                      value: 14,
                      id: "calcpi_max",
                      size: "small",
                    },
                  ],
                },
              },
            ],
          },
        ],
      },
      helpLink:
        "https://phoenixbioinformatics.atlassian.net/wiki/spaces/COM/pages/42217109/Protein+Search",
    };
  },
  watch: {
    "$route.query.key": function (id) {
      this.$forceUpdate();
    },
  },
  mounted() {
    // Reset restores these; not reactive state.
    this.initialValues = this.formElements().map((el) => copyValue(el.value));
  },
  methods: {
    async onSubmit(event) {
      event.preventDefault();
      let search_params = {};
      const nameBy = this.getValueById("name_search_by");
      const nameType = this.getValueById("name_search_type");
      const nameText = this.getValueById("name_input");
      const nameSection = {
        nameBy,
        nameType,
        nameText,
      };

      const classType = this.getValueById("class_type_multi");

      const calcmw_checkbox = this.getValueById("calcmw_checkbox");
      const calcpi_checkbox = this.getValueById("calcpi_checkbox");
      const length_checkbox = this.getValueById("length_checkbox");

      const physioSection = [];
      if (length_checkbox) {
        const length_min = this.getValueById("length_min");
        const length_max = this.getValueById("length_max");
        physioSection.push({
          type: "length",
          min: length_min,
          max: length_max,
        });
      }
      if (calcmw_checkbox) {
        const calcmw_min = this.getValueById("calcmw_min") * 1000;
        const calcmw_max = this.getValueById("calcmw_max") * 1000;
        physioSection.push({
          type: "mw",
          min: calcmw_min,
          max: calcmw_max,
        });
      }
      if (calcpi_checkbox) {
        const calcpi_min = this.getValueById("calcpi_min");
        const calcpi_max = this.getValueById("calcpi_max");
        physioSection.push({
          type: "pi",
          min: calcpi_min,
          max: calcpi_max,
        });
      }

      const { domainSection, error: domainError } = domainRowsToParams(
        this.domainRows
      );
      this.domainError = domainError || "";
      if (domainError) return;

      // const geneList = "AT5G64930;AT3G27880";
      let geneList = this.getValueById("gene_list");
      geneList = geneList.replace(/\r/g, "");
      geneList = geneList.replace(/\n/g, ";");

      search_params = {
        nameSection,
        geneList,
        classType,
        physioSection,
        domainSection,
      };

      this.showResults(search_params);
    },
    onReset(event) {
      event.preventDefault();
      this.formElements().forEach((element, i) => {
        element.value = copyValue(this.initialValues[i]);
      });
      this.domainRows = [newDomainRow()];
      this.domainError = "";
    },
    formElements() {
      const elements = [];
      this.search_form.filters.forEach((filter) => {
        if (filter.type === "rows") {
          filter.rows.forEach((row) => elements.push(...row.value.elements));
        }
      });
      return elements;
    },
    showResults(inp_params) {
      this.$nextTick(() => {
        let key = "searchParams_" + Date.now();
        localStorage.setItem(key, JSON.stringify(inp_params));
        this.$router.push({
          path: "/results",
          query: {
            mainType: "detail",
            category: "protein",
            key: key,
          },
        });
      });
    },
    getValueById(searchId) {
      let result = null;

      // Loop over each filter
      for (let filter of this.search_form.filters) {
        // Loop over each row in the current filter
        for (let row of filter.rows) {
          // Check if row has a value and elements
          if (row.value && row.value.elements) {
            // Find the element with the matching id
            let element = row.value.elements.find((el) => el.id === searchId);
            if (element) {
              result = element.value;
              break;
            }
          }
        }

        if (result !== null) {
          break;
        }
      }

      return result;
    },
  },
};
</script>

<style scoped lang="scss"></style>
